import axios, { AxiosResponse } from "axios";
import { checkSiteStatus } from "../utilities/utilities";

let url = process.env.REACT_APP_BACKEND_URL;

// REACT_APP_BACKEND_URL viene siempre con el sufijo /v1 y no se puede cambiar,
// asi que para los endpoints v2 reemplazamos la version en el path.
const toV2 = (base?: string) =>
  base ? `${base.replace(/\/+$/, '').replace(/\/v1$/, '')}/v2` : base;

const urlV2 = toV2(url);

export const Client = axios.create({
  baseURL: url,
  withCredentials: true
});

export const ClientV2 = axios.create({
  baseURL: urlV2,
  withCredentials: true
});

// axios ya parsea el body JSON, pero si el servidor no manda content-type
// correcto llega como string.
const parseErrorBody = (data: any) => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return data ?? null;
};

// Mismo criterio que usa axios para unir baseURL + url.
const combineURLs = (base: string, relative: string) =>
  relative
    ? `${base.replace(/\/+$/, '')}/${relative.replace(/^\/+/, '')}`
    : base;

// La fila esta asociada a paths puntuales del BFF y no a todo el sitio, asi que
// hay que chequear exactamente la URL que fallo, no el origin.
const buildProbeUrl = (config: any): string => {
  const path: string = config?.url ?? '';
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const base: string = config?.baseURL ?? url ?? window.location.origin;
  return combineURLs(base, path);
};

const QUEUEIT_AJAX_HEADER = 'x-queueit-ajaxpageurl';
const QUEUEIT_REDIRECT_HEADER = 'x-queueit-redirect';

// El header no es safelisted, asi que fuerza un preflight OPTIONS. En los POST
// y en /purchase/* da lo mismo (ya preflightean por content-type y por token),
// pero en los GET simples es un round trip extra que se puede evitar.
//
// La lista es de paths que NO estan detras de la fila, no al reves: mandar el
// header de mas solo cuesta un preflight, no mandarlo en un path encolado
// rompe la request (302 a queue-it.net -> CORS). Ante la duda, mandarlo.
// Tiene que reflejar los triggers configurados en Go Queue-it.
const QUEUE_FREE_PATHS = [
  /^\/?provinces(\/|\?|$)/,
  /^\/?localities(\/|\?|$)/,
  /^\/?communities(\/|\?|$)/,
  /^\/?help-text\/type(\/|\?|$)/,
  /^\/?analytics(\/|\?|$)/,
];

const needsQueueHeader = (rawUrl: string) => {
  const path = rawUrl.replace(/^https?:\/\/[^/]+/i, '');
  return !QUEUE_FREE_PATHS.some((pattern) => pattern.test(path));
};

// Sin este header el connector del edge no reconoce la request como AJAX y
// responde 302 a queue-it.net: el browser sigue el redirect dentro del XHR y
// muere por CORS. Con el header responde 200 + x-queueit-redirect.
// El valor va url-encodeado, que es como lo espera el SDK.
const registerQueueAjaxHeader = (client: typeof Client) =>
  client.interceptors.request.use((config) => {
    if (needsQueueHeader(config.url ?? '')) {
      config.headers.set(QUEUEIT_AJAX_HEADER, encodeURIComponent(window.location.href));
    }
    return config;
  });

const QUEUE_RELOAD_KEY = 'queue_reload_at';
const QUEUE_RELOAD_WINDOW_MS = 10_000;

// getAjaxRedirectUrl() devuelve la url encodeada.
// El enqueuetoken de la fila vence a los pocos minutos, asi que navegamos ya.
const goToQueue = (redirect: string) => {
  sessionStorage.removeItem(QUEUE_RELOAD_KEY);
  window.location.href = decodeURIComponent(redirect);
  // no resolvemos nunca: ya estamos saliendo de la pagina y no queremos que el
  // caller siga procesando una respuesta vacia
  return new Promise<AxiosResponse>(() => {});
};

// Si la fila sigue respondiendo 302 despues de recargar entrariamos en loop,
// asi que solo recargamos una vez cada QUEUE_RELOAD_WINDOW_MS.
const reloadOnce = () => {
  const last = Number(sessionStorage.getItem(QUEUE_RELOAD_KEY) ?? 0);
  if (Date.now() - last < QUEUE_RELOAD_WINDOW_MS) {
    return;
  }
  sessionStorage.setItem(QUEUE_RELOAD_KEY, String(Date.now()));
  window.location.reload();
};

// Interceptor para manejar errores 405 y 403 de fila virtual
const registerQueueInterceptor = (client: typeof Client) =>
  client.interceptors.response.use(
    (response) => {
      // el connector encola devolviendo 200 con este header y body vacio,
      // no un 302: cualquier endpoint del BFF puede traerlo
      const redirect: string | undefined = response.headers?.[QUEUEIT_REDIRECT_HEADER];
      if (redirect) {
        return goToQueue(redirect);
      }
      // volvimos a pasar la fila, habilitamos la proxima recarga
      sessionStorage.removeItem(QUEUE_RELOAD_KEY);
      return response;
    },
    async (error) => {

      // La fila virtual responde 403 cuando el acceso expiró: recargamos para
      // volver a entrar a la fila.
      if (error.response?.status === 403) {
        const body = parseErrorBody(error.response.data);
        if (body?.error === 'virtual_queue') {
          reloadOnce();
          return Promise.reject(error);
        }
      }

      if (!error.response?.status) {
        const isGet = (error.config?.method ?? 'get').toLowerCase() === 'get';
        const result = await checkSiteStatus(buildProbeUrl(error.config));
        // el chequeo siempre va por GET: un 405 sobre un endpoint que solo acepta
        // POST no significa que la fila este levantada
        if(result === 302 || (result === 405 && isGet))  {
          reloadOnce();
        }
      }
      return Promise.reject(error);
    }
  );

registerQueueAjaxHeader(Client);
registerQueueAjaxHeader(ClientV2);
registerQueueInterceptor(Client);
registerQueueInterceptor(ClientV2);